// Shared action-card render function. Both the in-game actions modal
// (_renderActionCard in game-map.js) and the prep-phase rest-action
// banner (rest-action.js) call this to get the same DOM shape.
//
// Pair with `actionCardStyles` from ./action-card-styles.js — that file
// owns the visual vocabulary (.menu-item.rich + sub-classes); this file
// owns the structure. Components using either MUST include actionCardStyles
// in their static styles.

import { html } from 'lit';
import { renderActionIcon } from './action-icons.js';

/**
 * @typedef {object} ActionCardBadge
 * @property {string} label
 * @property {string} [tone]          - bootstrap badge tone, e.g. "secondary", "info"
 * @property {string} [title]         - native browser tooltip
 * @property {string} [rulesSummary]  - if set, renders as a Bootstrap tooltip (data-bs-toggle)
 */

/**
 * @typedef {object} ActionCardProps
 * @property {string} [iconSlug]                    - action-icons.js registry slug; falls back to "action".
 * @property {string} title
 * @property {string} [description]                  - small, italic — rules text.
 * @property {string} [summary]                      - one-line summary in the body's emphasis text.
 * @property {string} [rulesSummary]                 - if set, renders an info icon with this as a tooltip.
 * @property {boolean} [enabled=true]
 * @property {string} [disabledReason]               - shown as an overlay on hover when disabled.
 * @property {Array<ActionCardBadge>} [badges]       - bottom-row badges (cost, effects, etc.).
 * @property {string} [bottomRowRight]               - right-aligned suffix in the bottom row (e.g., "X/Y slots", "X/Y selected").
 * @property {(e: Event) => void} [onClick]
 */

/**
 * Render a `.menu-item.rich` action card. Returns a Lit template; the host
 * component must include `actionCardStyles` in its `static styles`.
 * @param {ActionCardProps} props
 */
export function renderActionCard(props) {
  const {
    iconSlug,
    title,
    description,
    summary,
    rulesSummary,
    enabled = true,
    disabledReason,
    badges,
    bottomRowRight,
    onClick,
  } = props;

  const showDisabledOverlay = !enabled && disabledReason;
  const showBottomRow = (badges && badges.length > 0) || bottomRowRight;

  return html`
    <button
      class="menu-item rich ${!enabled ? 'is-disabled' : ''}"
      ?disabled="${!enabled}"
      @click="${onClick}"
    >
      ${
        showDisabledOverlay
          ? html`<div class="action-disabled-overlay">
            <span class="action-disabled-overlay-icon">${renderActionIcon('ban')}</span>
            <span>${disabledReason}</span>
          </div>`
          : ''
      }
      <div class="action-row">
        <span class="action-icon">${renderActionIcon(iconSlug)}</span>
        <span class="action-title">${title}</span>
        ${
          rulesSummary
            ? html`<span
              class="action-info-icon"
              data-bs-toggle="tooltip"
              data-bs-html="true"
              data-bs-title="${rulesSummary}"
              >${renderActionIcon('info')}</span
            >`
            : ''
        }
        ${showDisabledOverlay ? html`<span class="action-disabled-icon">${renderActionIcon('ban')}</span>` : ''}
      </div>
      ${description ? html`<div class="action-description">${description}</div>` : ''}
      ${summary ? html`<div class="action-summary">${summary}</div>` : ''}
      ${
        showBottomRow
          ? html`<div class="bottom-row">
            ${badges?.map((b) =>
              b.rulesSummary
                ? html`<span
                    class="badge ${b.tone ? `text-bg-${b.tone}` : 'text-bg-secondary'}"
                    data-bs-toggle="tooltip"
                    data-bs-title="${b.rulesSummary}"
                    >${b.label}</span
                  >`
                : html`<span
                    class="badge ${b.tone ? `text-bg-${b.tone}` : 'text-bg-secondary'}"
                    title="${b.title || ''}"
                    >${b.label}</span
                  >`,
            )}
            ${bottomRowRight ? html`<span class="bottom-row-uses">${bottomRowRight}</span>` : ''}
          </div>`
          : ''
      }
    </button>
  `;
}
